import api from './api';
import type {
  PaginatedResponse,
  JournalStats,
  JournalKPIs,
  EquityPoint,
  CalendarDay,
  PnlByHour,
  PnlByDayOfWeek,
  MonthlyPnl,
  TradeSource,
  InstrumentType,
  TradeDirection,
  TradeResult,
  BulkCreateResult,
} from '@trading/shared';
import type { DateRange } from '../lib/journal/types';

// --- API Types (camelCase, UUID-based, matches backend REST API) ---

export interface ApiJournalTrade {
  id: string;
  userId: string;
  accountId: string;
  playbookId?: string | null;
  analysisId?: string | null;
  source: TradeSource;
  instrumentType: InstrumentType;
  symbol: string;
  direction: TradeDirection;
  result: TradeResult;
  entryPrice: number;
  exitPrice?: number | null;
  stopLoss?: number | null;
  takeProfit?: number | null;
  quantity?: number | null;
  fees: number;
  grossPnl: number;
  netPnl: number;
  pnlPercent?: number | null;
  pnlPips?: number | null;
  notes?: string | null;
  tags: string[];
  timeframe?: string | null;
  entryDate: string;
  exitDate?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ApiJournalTradeCreate {
  playbookId?: string | null;
  analysisId?: string | null;
  source?: TradeSource;
  instrumentType: InstrumentType;
  symbol: string;
  direction: TradeDirection;
  entryPrice: number;
  exitPrice?: number | null;
  stopLoss?: number | null;
  takeProfit?: number | null;
  quantity?: number | null;
  fees?: number;
  /** Optional direct net P&L override. When provided, bypasses price-based computation. */
  netPnl?: number | null;
  notes?: string | null;
  tags?: string[];
  timeframe?: string | null;
  entryDate: string;
  exitDate?: string | null;
  // accountId is intentionally absent: the backend derives it from auth context
  // (the user's default account). Callers do not need to supply it.
  //
  // result is intentionally absent: the backend always computes it from
  // exitPrice vs entryPrice and direction. Supplying it would be ignored.
}

export interface ApiJournalTradeUpdate {
  playbookId?: string | null;
  instrumentType?: InstrumentType;
  symbol?: string;
  direction?: TradeDirection;
  entryPrice?: number;
  exitPrice?: number | null;
  stopLoss?: number | null;
  takeProfit?: number | null;
  quantity?: number | null;
  fees?: number;
  /** Optional direct net P&L override. When provided, bypasses price-based computation. */
  netPnl?: number | null;
  notes?: string | null;
  tags?: string[];
  timeframe?: string | null;
  entryDate?: string;
  exitDate?: string | null;
}

export interface ApiJournalPlaybook {
  id: string;
  userId: string;
  name: string;
  description?: string | null;
  rules: string;
  createdAt: string;
  updatedAt: string;
}

export interface ApiJournalPlaybookCreate {
  name: string;
  description?: string;
  rules: string;
}

export interface ApiJournalPlaybookUpdate {
  name?: string;
  description?: string;
  rules?: string;
}

// Re-export analytics and result types from @trading/shared for consumers that import them here
export type { JournalKPIs, EquityPoint, CalendarDay, PnlByHour, PnlByDayOfWeek, MonthlyPnl, BulkCreateResult };

/** ApiJournalStats is the stats response shape — identical to JournalStats from @trading/shared. */
export type ApiJournalStats = JournalStats;

/** Valid sort columns accepted by the /journal/trades endpoint. */
export type TradeSortField =
  | 'entryDate'
  | 'symbol'
  | 'direction'
  | 'entryPrice'
  | 'exitPrice'
  | 'quantity'
  | 'netPnl'
  | 'result';

export interface TradeQueryParams {
  page?: number;
  limit?: number;
  accountId?: string | null;
  symbol?: string;
  direction?: TradeDirection;
  /** Single value ('WIN') or comma-separated shorthand ('WIN,LOSS,BREAKEVEN'). */
  result?: string;
  instrumentType?: InstrumentType;
  playbookId?: string | null;
  startDate?: string;
  endDate?: string;
  sortBy?: TradeSortField;
  sortOrder?: 'asc' | 'desc';
}

/** Alias for TradeQueryParams — used by page components for query param construction. */
export type FetchTradesParams = TradeQueryParams;

export interface StatsQueryParams {
  accountId?: string | null;
  startDate?: string;
  endDate?: string;
}

/**
 * Convert a DateRange preset to startDate/endDate query params.
 * All presets are rolling windows anchored at "now".
 */
export function dateRangeToParams(range: DateRange): { startDate?: string; endDate?: string } {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (range === 'all') {
    return {};
  }

  let startDate: Date;
  let endDate: Date = now;

  switch (range) {
    case 'today':
      startDate = today;
      break;
    case 'yesterday': {
      startDate = new Date(today);
      startDate.setDate(startDate.getDate() - 1);
      endDate = today; // end of yesterday = start of today
      break;
    }
    case 'week':
      startDate = new Date(today);
      startDate.setDate(startDate.getDate() - 7);
      break;
    case 'month':
      startDate = new Date(today);
      startDate.setMonth(startDate.getMonth() - 1);
      break;
    case 'mtd':
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      break;
    case 'year':
      startDate = new Date(today);
      startDate.setFullYear(startDate.getFullYear() - 1);
      break;
    case 'ytd':
      startDate = new Date(now.getFullYear(), 0, 1);
      break;
    default:
      startDate = new Date(today);
      startDate.setMonth(startDate.getMonth() - 1);
      break;
  }

  return {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  };
}

// --- Service ---

// --- Open Positions ---

export interface ApiOpenPosition {
  id: string;
  brokerTicket: string;
  symbol: string;
  side: 'LONG' | 'SHORT';
  volume: number;
  entryPrice: number;
  currentPrice: number | null;
  stopLoss: number | null;
  takeProfit: number | null;
  openedAt: string;
  capturedAt: string;
  unrealizedPnL: number | null;
}

export const journalService = {
  // Open Positions

  async getOpenPositions(): Promise<ApiOpenPosition[]> {
    // The api.ts response interceptor already unwraps the
    // `{ success, data }` envelope, so response.data IS the array.
    const response = await api.get<ApiOpenPosition[]>('/journal/open-positions');
    return response.data;
  },

  // Trades

  async getTrades(params?: TradeQueryParams): Promise<PaginatedResponse<ApiJournalTrade>> {
    const response = await api.get<PaginatedResponse<ApiJournalTrade>>(
      '/journal/trades',
      { params }
    );
    return response.data;
  },

  async getTrade(id: string): Promise<ApiJournalTrade> {
    const response = await api.get<ApiJournalTrade>(`/journal/trades/${id}`);
    return response.data;
  },

  async createTrade(data: ApiJournalTradeCreate): Promise<ApiJournalTrade> {
    const response = await api.post<ApiJournalTrade>('/journal/trades', data);
    return response.data;
  },

  async updateTrade(id: string, data: ApiJournalTradeUpdate): Promise<ApiJournalTrade> {
    const response = await api.put<ApiJournalTrade>(`/journal/trades/${id}`, data);
    return response.data;
  },

  async deleteTrade(id: string): Promise<void> {
    await api.delete(`/journal/trades/${id}`);
  },

  async bulkCreateTrades(trades: ApiJournalTradeCreate[]): Promise<BulkCreateResult> {
    const response = await api.post<BulkCreateResult>('/journal/trades/bulk', trades);
    return response.data;
  },

  // Stats

  async getStats(params?: StatsQueryParams): Promise<ApiJournalStats> {
    const response = await api.get<ApiJournalStats>('/journal/stats', { params });
    return response.data;
  },

  // Playbooks

  async getPlaybooks(params?: { page?: number; limit?: number }): Promise<PaginatedResponse<ApiJournalPlaybook>> {
    const response = await api.get<PaginatedResponse<ApiJournalPlaybook>>(
      '/journal/playbooks',
      { params }
    );
    return response.data;
  },

  async createPlaybook(data: ApiJournalPlaybookCreate): Promise<ApiJournalPlaybook> {
    const response = await api.post<ApiJournalPlaybook>('/journal/playbooks', data);
    return response.data;
  },

  async updatePlaybook(id: string, data: ApiJournalPlaybookUpdate): Promise<ApiJournalPlaybook> {
    const response = await api.put<ApiJournalPlaybook>(`/journal/playbooks/${id}`, data);
    return response.data;
  },

  async deletePlaybook(id: string): Promise<void> {
    await api.delete(`/journal/playbooks/${id}`);
  },
};
